import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import axios from "axios";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import MarkdownArticle from "../components/ui/MarkdownArticle";
import { ArrowLeft, Clock, Eye, Image as ImageIcon, BookOpen } from "lucide-react";

/* -----------------------------
   Utils
----------------------------- */

function formatDateBR(dateStr) {
  if (!dateStr) return "";
  try {
    // Extrai apenas YYYY-MM-DD de qualquer formato (ISO, timestamp, etc.)
    const match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return dateStr;

    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    if (isNaN(date.getTime())) return dateStr;

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return dateStr;
  }
}

function isValidHttpUrl(u) {
  try {
    const url = new URL(u);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeUrl(u) {
  if (!u) return "";
  return String(u).trim().replace(/^<|>$/g, "");
}

/**
 * Markdown image extractor que respeita parênteses na URL:
 * ![alt](url "title")
 * ![alt](<url>)
 */
function extractMarkdownImages(content) {
  const out = [];
  if (!content) return out;

  const s = String(content);
  const n = s.length;

  let i = 0;
  while (i < n) {
    const bang = s.indexOf("![", i);
    if (bang === -1) break;

    const altStart = bang + 2;
    const altEnd = s.indexOf("]", altStart);
    if (altEnd === -1) break;

    // spaces then "("
    let j = altEnd + 1;
    while (j < n && /\s/.test(s[j])) j++;
    if (s[j] !== "(") {
      i = altEnd + 1;
      continue;
    }

    j++; // after "("
    while (j < n && /\s/.test(s[j])) j++;

    const alt = s.slice(altStart, altEnd).trim();

    // Case 1: <...>
    if (s[j] === "<") {
      j++;
      const k = s.indexOf(">", j);
      if (k === -1) {
        i = altEnd + 1;
        continue;
      }

      let url = s.slice(j, k).trim();
      url = normalizeUrl(url);

      j = k + 1;
      while (j < n && s[j] !== ")") j++;
      if (s[j] === ")") j++;

      i = j;

      if (isValidHttpUrl(url)) out.push({ url, alt });
      continue;
    }

    // Case 2: raw url with balanced ()
    const urlStart = j;
    let balance = 0;

    while (j < n) {
      const ch = s[j];

      if (ch === "\\") {
        j += 2;
        continue;
      }
      if (ch === "(") {
        balance++;
        j++;
        continue;
      }
      if (ch === ")") {
        if (balance === 0) break;
        balance--;
        j++;
        continue;
      }
      // title starts with whitespace outside balance
      if (/\s/.test(ch) && balance === 0) break;

      j++;
    }

    let url = s.slice(urlStart, j).trim();

    // skip whitespace + optional title
    while (j < n && /\s/.test(s[j])) j++;

    if (s[j] === `"` || s[j] === `'`) {
      const quote = s[j++];
      while (j < n && s[j] !== quote) j++;
      if (s[j] === quote) j++;
      while (j < n && /\s/.test(s[j])) j++;
    } else if (s[j] === "(") {
      // title in parentheses (rare)
      let tb = 1;
      j++;
      while (j < n && tb > 0) {
        if (s[j] === "(") tb++;
        else if (s[j] === ")") tb--;
        j++;
      }
      while (j < n && /\s/.test(s[j])) j++;
    }

    if (s[j] === ")") j++;
    i = j;

    url = normalizeUrl(url);
    if (isValidHttpUrl(url)) out.push({ url, alt });
  }

  return out;
}

function extractImagesFromContent(content) {
  const images = [];
  if (!content) return images;

  images.push(...extractMarkdownImages(content));

  // HTML <img src="...">
  const htmlRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;
  while ((match = htmlRegex.exec(content)) !== null) {
    const url = normalizeUrl(match[1]);
    if (isValidHttpUrl(url)) images.push({ url, alt: "" });
  }

  return images;
}

/**
 * Remove do texto:
 * - linhas que são só imagem markdown
 * - tags <img>
 * - headings de imagens/galeria
 * - legendas repetidas (Registro visual do fato...)
 */
function cleanContentForReading(content) {
  if (!content) return "";

  let out = String(content);

  // remove linhas que são só imagem markdown (com/sem bullet)
  out = out.replace(/^\s*[-*]?\s*!\[[^\]]*\]\([^)]+\)\s*$/gim, "");

  // remove tags <img ...>
  out = out.replace(/<img[^>]*>/gim, "");

  // remove headings específicos
  out = out.replace(/^\s*#{1,6}\s*Imagens da situa[cç][aã]o\s*$/gim, "");
  out = out.replace(/^\s*#{1,6}\s*Galeria de imagens\s*$/gim, "");
  out = out.replace(/^\s*#{1,6}\s*Galeria\s*$/gim, "");

  // remove legendas repetidas
  out = out.replace(/^\s*\*?Registro visual do fato \(Img \d+\)\*?\s*$/gim, "");

  // normaliza quebras
  out = out.replace(/\n{3,}/g, "\n\n").trim();

  return out;
}

function uniqByUrl(list) {
  const seen = new Set();
  const out = [];
  for (const item of list || []) {
    const u = (item?.url || "").trim();
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push({ url: u, alt: (item?.alt || "").trim() });
  }
  return out;
}

/* -----------------------------
   Page
----------------------------- */

export default function NoticiaDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentHeroImage, setCurrentHeroImage] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());

  const viewRegistered = useRef(null);

  const fetchNoticia = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/news/${slug}`);
      if (res.data?.success) {
        setNoticia(res.data.data);
      } else {
        setNoticia(null);
      }
    } catch (err) {
      console.error("Erro ao carregar notícia", err);
      setNoticia(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchNoticia();
    // Reset estados de imagem quando a notícia mudar
    setCurrentHeroImage(null);
    setFailedImages(new Set());
  }, [fetchNoticia]);

  // Registra visita apenas uma vez por slug
  useEffect(() => {
    if (!slug || viewRegistered.current === slug) return;
    viewRegistered.current = slug;
    axios.post(`/news/${slug}/view`).catch(() => {});
  }, [slug]);

  // Imagens vindas da API
  const apiImages = useMemo(() => {
    if (!noticia?.images?.length) return [];
    return noticia.images
      .map((img) => ({
        url: normalizeUrl(img?.url),
        alt: (img?.alt || "").trim(),
      }))
      .filter((x) => x.url && isValidHttpUrl(x.url));
  }, [noticia?.images]);

  // Imagens vindas do conteúdo (markdown/html)
  const contentImages = useMemo(() => {
    const imgs = extractImagesFromContent(noticia?.content || "");
    return imgs
      .map((img) => ({
        url: normalizeUrl(img?.url),
        alt: (img?.alt || "").trim(),
      }))
      .filter((x) => x.url && isValidHttpUrl(x.url));
  }, [noticia?.content]);

  // União sem duplicar
  const allImages = useMemo(() => uniqByUrl([...apiImages, ...contentImages]), [apiImages, contentImages]);

  // Featured (hero): preferir imageUrl da API, senão primeira imagem válida
  const initialFeaturedImage = useMemo(() => {
    const u = normalizeUrl(noticia?.imageUrl || "");
    if (u && isValidHttpUrl(u)) return u;
    return allImages[0]?.url || null;
  }, [noticia?.imageUrl, allImages]);

  // Imagem do hero atual (pode mudar se a anterior falhar)
  const featuredImage = currentHeroImage || initialFeaturedImage;

  // Função para encontrar próxima imagem disponível quando o hero falhar
  const getNextAvailableImage = useCallback((currentImageUrl) => {
    if (!allImages.length) return null;

    // Filtra imagens que não falharam e não são a atual do hero
    const available = allImages.filter(
      img => !failedImages.has(img.url) && img.url !== currentImageUrl
    );

    return available[0]?.url || null;
  }, [allImages, failedImages]);

  // Conteúdo sem “blocos feios” de imagens/legendas
  const cleanedContent = useMemo(() => cleanContentForReading(noticia?.content || ""), [noticia?.content]);

  // Galeria (remove o featured para não duplicar)
  const galleryImages = useMemo(() => {
    const list = allImages || [];
    if (!list.length) return [];
    if (!featuredImage) return list;
    return list.filter((img) => img.url !== featuredImage);
  }, [allImages, featuredImage]);

  const imageCount = allImages.length;

  /* -----------------------------
     States
  ----------------------------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto mb-4" />
          <p className="text-[var(--muted)]">Carregando notícia...</p>
        </div>
      </div>
    );
  }

  if (!noticia) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-[var(--text)] mb-4">Notícia não encontrada</h2>
            <button
              onClick={() => navigate("/noticias")}
              className="inline-flex items-center gap-2 px-6 py-2 bg-[var(--accent)] text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <ArrowLeft size={18} />
              Voltar para o Feed
            </button>
          </div>
        </Card>
      </div>
    );
  }

  /* -----------------------------
     UI
  ----------------------------- */

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pb-24">
      <SEO
        title={noticia.title}
        description={`Notícia sobre ${noticia.categoria || "Geral"}: ${noticia.title}`}
        path={`/noticias/${slug}`}
        image={featuredImage || undefined}
        type="article"
      />
      {/* Back */}
      <div className="pt-5">
        <button
          onClick={() => navigate("/noticias")}
          className="inline-flex items-center gap-2 text-[var(--muted)] hover:text-[var(--accent)] transition-colors font-medium"
        >
          <ArrowLeft size={18} />
          <span>Voltar</span>
        </button>
      </div>

      <article className="mt-6">
        {/* Header (centralizado no mesmo width do Card) */}
        <header className="mx-auto max-w-3xl mb-5">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge category={noticia.categoria || "Geral"} />

            {noticia.dataGeracao && (
              <div className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)]">
                <Clock size={16} />
                <span>{formatDateBR(noticia.dataGeracao)}</span>
              </div>
            )}

            {noticia.views > 0 && (
              <div className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)]">
                <Eye size={16} />
                <span>{noticia.views.toLocaleString("pt-BR")} {noticia.views === 1 ? "visualização" : "visualizações"}</span>
              </div>
            )}

            <div className="text-sm text-[var(--muted)]">
              <span className="font-semibold text-[var(--text)]">Por Redação</span>
            </div>

            {imageCount > 0 && (
              <div className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)]">
                <ImageIcon size={16} />
                <span>
                  {imageCount} {imageCount === 1 ? "imagem" : "imagens"}
                </span>
              </div>
            )}
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold text-[var(--text)] leading-[1.06] tracking-tight">
            {noticia.title}
          </h1>

          {galleryImages.length > 0 && (
            <div className="mt-3">
              <a
                href="#galeria"
                className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors"
              >
                <ImageIcon size={16} />
                Ver galeria
              </a>
            </div>
          )}
        </header>

        {/* ✅ Um único Card contendo hero + texto + galeria (galeria no meio/fim) */}
        <div className="mx-auto max-w-3xl">
          <Card className="overflow-hidden">
            {/* Hero (tenta próxima imagem se falhar) */}
            {featuredImage && (
              <div className="border-b border-[var(--border)]">
                <div className="relative w-full aspect-[16/9] bg-[var(--card)]">
                  <img
                    src={featuredImage}
                    alt={noticia.title}
                    className="absolute inset-0 w-full h-full object-contain p-4 bg-white"
                    loading="eager"
                    referrerPolicy="no-referrer"
                    onError={() => {
                      // Marca a imagem atual como falha
                      setFailedImages(prev => new Set([...prev, featuredImage]));

                      // Tenta próxima imagem disponível
                      const nextImage = getNextAvailableImage(featuredImage);
                      if (nextImage) {
                        setCurrentHeroImage(nextImage);
                      } else {
                        // Se não houver mais imagens, remove o hero
                        setCurrentHeroImage(null);
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Conteúdo */}
            <div className="px-5 sm:px-7 lg:px-10 py-7">
              <div className="prose prose-slate max-w-none">
                <MarkdownArticle content={cleanedContent || noticia.content || ""} />
              </div>

              {/* Galeria (no meio/fim) */}
              {galleryImages.length > 0 && (
                <section id="galeria" className="mt-10 pt-8 border-t border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon size={18} className="text-[var(--muted)]" />
                    <h2 className="text-base font-bold text-[var(--text)]">Galeria</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {galleryImages.slice(0, 12).map((img, index) => {
                      if (failedImages.has(img.url)) return null;

                      return (
                        <figure
                          key={`${img.url}-${index}`}
                          className="rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--card)] shadow-sm"
                        >
                          <div className="relative w-full aspect-[16/10] bg-[var(--surface-alt)]">
                            <img
                              src={img.url}
                              alt={img.alt || `Imagem ${index + 1}`}
                              className="absolute inset-0 w-full h-full object-contain p-3 bg-white"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={() => {
                                setFailedImages(prev => new Set([...prev, img.url]));
                              }}
                            />
                          </div>

                          {img.alt ? (
                            <figcaption className="px-3 py-2 text-xs text-[var(--muted)] border-t border-[var(--border)]">
                              {img.alt}
                            </figcaption>
                          ) : null}
                        </figure>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Leia mais sobre o tema */}
              {noticia.relatedArticles?.length > 0 && (
                <section className="mt-10 pt-8 border-t border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-5">
                    <BookOpen size={18} className="text-[var(--accent)]" />
                    <h2 className="text-base font-bold text-[var(--text)]">Leia mais sobre o tema</h2>
                  </div>

                  <div className="flex flex-col gap-3">
                    {noticia.relatedArticles.map((rel) => (
                      <button
                        key={rel.slug}
                        onClick={() => navigate(`/noticias/${rel.slug}`)}
                        className="w-full text-left flex gap-4 p-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] hover:border-[var(--accent)] hover:shadow-md transition-all group"
                      >
                        {rel.imageUrl && (
                          <div className="hidden sm:block w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-[var(--card)]">
                            <img
                              src={rel.imageUrl}
                              alt={rel.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-[var(--text)] line-clamp-2 group-hover:text-[var(--accent)] transition-colors leading-snug">
                            {rel.title}
                          </h3>
                          {rel.summary && (
                            <p className="text-xs text-[var(--muted)] mt-1 line-clamp-2 leading-relaxed">
                              {rel.summary}
                            </p>
                          )}
                          {rel.dataGeracao && (
                            <span className="inline-flex items-center gap-1 text-xs text-[var(--muted)] mt-2">
                              <Clock size={12} />
                              {formatDateBR(rel.dataGeracao)}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </Card>
        </div>
      </article>
    </div>
  );
}
